export type InputModeExplainerStep =
  | 'cooked'
  | 'raw'
  | 'difference'
  | 'examples';

export const inputModesExplainerSteps = {
  cooked: {
    title: 'Cooked Mode (Line-Buffered)',
    description:
      'In cooked mode (also called canonical mode), the terminal collects your input into a line buffer. You can edit with backspace, and nothing is sent to the program until you press Enter. This is how your shell normally works.',
  },
  raw: {
    title: 'Raw Mode (Character-at-a-Time)',
    description:
      'In raw mode, every keystroke is sent to the program immediately—no buffering, no line editing. The program sees each key the instant you press it. This is how vim, htop, and other interactive programs work.',
  },
  difference: {
    title: 'Why Two Modes?',
    description:
      "Your terminal always sends the same bytes—the difference is how the kernel processes them. In cooked mode, the kernel's line discipline buffers input and handles editing. In raw mode, bytes pass straight through to the program. This lets simple programs get free line editing, while complex TUIs get full control.",
  },
  examples: {
    title: 'Real-World Examples',
    description:
      'Day to day my zsh session stays cooked—I finish a line before Enter fires. Vim, ssh, less flip to raw so every key matters immediately. Ctrl+C follows the same split: SIGINT via line discipline in cooked mode versus the raw-byte path when a TUI owns the tty.',
  },
} satisfies Record<
  InputModeExplainerStep,
  { title: string; description: string }
>;

export const inputModesDemoUi = {
  subsectionMode: 'Input Mode:',
  cookedToggle: 'Cooked (Line-Buffered)',
  rawToggle: 'Raw (Immediate)',
  cookedPanelTitle: 'Type, Edit, Then Enter',
  rawPanelTitle: 'Every Key Sent Instantly',
  rawEmpty: 'Start typing...',
  rawHint: 'Click here and type. Each key appears instantly.',
  clear: 'Clear',
  whatsHappening: "What's Happening",
  cookedModeLabel: 'Cooked Mode',
  canonical: '(canonical)',
  rawModeLabel: 'Raw Mode',
  nonCanonical: '(non-canonical)',
  comparison: {
    title: 'Cooked vs Raw: Quick Comparison',
    colBehavior: 'Behavior',
    colCooked: 'Cooked Mode',
    colRaw: 'Raw Mode',
    rows: [
      ['When input is sent', 'After pressing Enter', 'Immediately per key'],
      ['Backspace', 'Deletes from buffer', 'Just another key'],
      ['Ctrl+C', 'Line discipline generates SIGINT', 'Program receives 0x03'],
      ['Arrow keys', 'Line recall (history)', 'Program handles it'],
      ['Used by', 'bash, zsh, cat', 'vim, htop, ssh, less'],
    ],
  },
} as const;
